'use client';

import type React from 'react';
import { ArrowDown, ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { usePathname } from 'next/navigation';
import { motion } from 'framer-motion';
import { useScrollToSection } from '@/components/hooks/use-scroll-to-section';
import { Button } from '@/components/ui/button';
import Container from '@/components/ui/layout/container';
import Flex from '@/components/ui/layout/flex';
import Section from '@/components/ui/layout/section';
import { MotionLink } from '@/components/ui/Motion';
import {
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographySmall,
} from '@/components/ui/typography';
import { cls, formatForUrl, getServicePath } from '@/lib/utils';
import { INCLUDED_SECTION_ID } from '../Included/consts';
import { wrapperElementMotionProperties } from './animations';
import SERVICES_SECTION_CONTENT, {
  CTA_BUTTON_TEXT,
  SERVICES_SECTION_ID,
  SERVICES_SECTION_SUB_TITLE,
} from './consts';

const HREF = `#SECTION-${INCLUDED_SECTION_ID}`;

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  },
};

function getIcon(iconName: string) {
  const icons = LucideIcons as unknown as Record<
    string,
    React.ComponentType<{ className?: string }>
  >;
  const Icon = icons[iconName];
  return Icon || LucideIcons.Home;
}

export default function ServicesSection() {
  const pathname = usePathname();
  const servicePath = getServicePath(pathname);
  const handleOnClick = useScrollToSection(HREF);

  if (servicePath === null) {
    return null;
  }

  const { title, description, services } =
    SERVICES_SECTION_CONTENT[servicePath];

  return (
    <Section
      id={SERVICES_SECTION_ID}
      className="bg-card"
      {...wrapperElementMotionProperties}
    >
      <Container
        id={SERVICES_SECTION_ID}
        maxWidth="max-w-5xl"
        sm="sm:px-4 sm:py-8"
        md="md:px-8 md:py-12"
        lg="lg:px-12 lg:py-16"
      >
        <Container maxWidth="max-w-2xl" className="m-0">
          <Flex
            direction="column"
            items="start"
            gap="gap-2 sm:gap-2 md:gap-4 lg:gap-6"
            className="flex-1"
          >
            <TypographyH3 className={cls`font-bold text-primary`}>
              {SERVICES_SECTION_SUB_TITLE}
            </TypographyH3>
            <TypographyH2 className="border-b-0">{title}</TypographyH2>
            <TypographySmall className={cls`m-0!`}>
              {description}
            </TypographySmall>

            <Button
              className={cls`w-full md:w-fit`}
              variant="secondary"
              onClick={handleOnClick}
              asChild
            >
              <MotionLink href={HREF}>
                <ArrowDown className={cls`mr-1`} />
                {CTA_BUTTON_TEXT}
              </MotionLink>
            </Button>
          </Flex>
        </Container>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 py-8 sm:py-8 md:py-12 lg:py-16 w-full"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1, delayChildren: 0.2 },
            },
          }}
        >
          {services.map((service) => {
            const Icon = getIcon(service.icon);
            return (
              <motion.div key={service.name} {...cardMotionProps}>
                <MotionLink
                  href={`${pathname}/${formatForUrl(service.name)}`}
                  className={cls`
                    group flex flex-col gap-3
                    p-6 rounded-lg
                    bg-background
                    border border-border
                    hover:border-primary/40
                    transition-all duration-300
                    h-full
                  `}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="text-primary size-7" />
                    <ArrowRight className="text-muted-foreground size-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                  <TypographyH4 className={cls`m-0! text-foreground`}>
                    {service.name}
                  </TypographyH4>
                  <TypographySmall className="text-muted-foreground leading-relaxed m-0!">
                    {service.tagline}
                  </TypographySmall>
                </MotionLink>
              </motion.div>
            );
          })}
        </motion.div>
      </Container>
    </Section>
  );
}
